import { zodResolver } from '@hookform/resolvers/zod'
import React, { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { SsoProvidersList } from '@/components/SsoProviderButton/SsoProvidersList'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { AppRoutes } from '@/constants/appRoutes'
import {
  finalSession,
  loginResultSchema,
  securityErrorKey,
  useSensitiveRequest,
} from '@/features/security/securityClient'
import type {
  FinalSession,
  MfaChallenge,
} from '@/features/security/securityClient'
import { useAppDispatch } from '@/hooks/redux'
import { setSession } from '@/store/api/Auth/Auth.slice'
import { setUserCredentials } from '@/store/api/SignUp/SignUp.slice'
import type { LoginType } from '@/utils/schemas'
import { loginSchema } from '@/utils/schemas'

import { MfaChallengeForm } from './MfaChallengeForm'

export const LoginForm = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const form = useForm<LoginType>({
    resolver: zodResolver(loginSchema),

    defaultValues: {
      email: '',
      password: '',
    },
  })

  const {
    handleSubmit,
    control,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: { errors = {} },
  } = form

  const request = useSensitiveRequest()
  const [isLoading, setIsLoading] = useState(false)
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null)
  const [error, setError] = useState('')
  const lock = useRef(false)

  const finishLogin = (session: FinalSession, email: string) => {
    dispatch(setSession(finalSession(session)))
    if (session.userStatus === 'active') {
      navigate(AppRoutes.dashboard)
    } else {
      dispatch(setUserCredentials({ email, authToken: session.token }))
      navigate(AppRoutes.registerEmailVerification)
    }
  }

  const makeLogin = async (password: string, email: string) => {
    if (lock.current) return
    lock.current = true
    setIsLoading(true)
    setError('')
    try {
      const result = await request('/api/v1/auth/sign-in', loginResultSchema, {
        body: { email, password },
      })
      form.resetField('password')
      if ('mfaRequired' in result && result.mfaRequired) {
        setChallenge(result)
      } else {
        finishLogin(result as FinalSession, email)
      }
    } catch (e) {
      form.resetField('password')
      setError(securityErrorKey(e))
    } finally {
      lock.current = false
      setIsLoading(false)
    }
  }

  const onSubmit = (data: any) => {
    makeLogin(data.password, data.email)
  }

  if (challenge)
    return (
      <MfaChallengeForm
        challenge={challenge}
        onComplete={(session) => finishLogin(session, form.getValues('email'))}
        onCancel={() => {
          setChallenge(null)
          setError('')
          form.resetField('password')
        }}
      />
    )

  return (
    <div className="flex flex-col gap-8 w-full max-w-[436px]">
      <div className="flex flex-col gap-5">
        <p className="text-[40px] font-bold tracking-wide text-center">
          {t('dashboard.landingLogIn.login')}
        </p>
        <p className="text-1xl text-textAccent tracking-tight text-center">
          {t('dashboard.landingLogIn.welcomeBack')}
        </p>
      </div>

      <SsoProvidersList />
      {error && (
        <p role="alert" className="text-dynamicRed">
          {t(error)}
        </p>
      )}
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-neutralColor30" />
        <span className="text-xs uppercase tracking-widest text-textAccent">
          {t('auth.or_separator')}
        </span>
        <span className="h-px flex-1 bg-neutralColor30" />
      </div>

      <Form {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="sentry-block flex flex-col gap-6"
          data-sentry-block="true"
        >
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem className="w-full bg-white rounded-md">
                <FormLabel>{t('dashboard.account.email')}</FormLabel>
                <FormControl className="w-full focus:outline-none">
                  <Input
                    {...field}
                    autoComplete="username"
                    placeholder={t('dashboard.landingLogIn.example')}
                  />
                </FormControl>
                <FormMessage data-name="email" />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="password"
            render={({ field }) => (
              <FormItem className="w-full bg-white rounded-md  ">
                <FormLabel>{t('dashboard.landingLogIn.password')}</FormLabel>
                <FormControl className="w-full focus:outline-none">
                  <Input
                    {...field}
                    placeholder={t('dashboard.landingLogIn.password')}
                    type="password"
                    autoComplete="current-password"
                    showPassIcon
                  />
                </FormControl>
                <FormMessage data-name="password" />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            className="py-3 px-5 font-medium tracking-widest h-11"
            isLoading={isLoading}
          >
            {t('dashboard.landingLogIn.login')}
          </Button>
        </form>
      </Form>
      <div className="text-center max-w-[436px]">
        <p className="pb-6 text-center text-linkColor font-medium">
          <Link to={AppRoutes.forgotPassword}>
            {t('dashboard.landingLogIn.forgotPassword')}
          </Link>
        </p>
      </div>
    </div>
  )
}
